import { apiClient } from "./client";

/**
 * Dashboard summary — analytics + health'i tek istekte değil de
 * paralel fetch yapacağız; bu dosya tip uniforması için.
 */
export type DashboardSpofCard = {
  provider: string;
  level: "ok" | "warning" | "critical";
  active_keys: number;
  min_recommended: number;
  message: string;
};

export type DashboardCardSet = {
  users: { total: number; new_last_30d: number; suspended: number; setup_completed: number };
  revenue: {
    active_subscriptions: number;
    mrr_usd: number;
    arr_usd: number;
    subscribers_by_plan: Record<string, number>;
  };
  usage: {
    days: number;
    contents_created: number;
    contents_ready: number;
    total_cost_usd: number;
    total_calls: number;
    total_tokens: number;
    total_words: number;
  };
  health: {
    checked_at: string;
    stale_heartbeats: number;
    anomalies_24h: number;
    recoveries_24h: number;
    in_flight_generation_jobs: number;
    active_in_flight_contents: number;
  };
  // F11 cluster K — surface pool SPOF on the operator dashboard so the
  // single-key Anthropic risk doesn't only live inside /api-pool.
  spof: DashboardSpofCard[];
};

type PoolStatsResponse = {
  provider: string;
  stats?: {
    spof?: {
      level: "ok" | "warning" | "critical";
      message: string;
      active_keys: number;
      min_recommended: number;
    };
  };
};

// V_DEEPSEEK_ONLY — sistem tek LLM sağlayıcılı (DeepSeek). SPOF kart
// listesinde Anthropic kalkti; geriye sadece DeepSeek (primary LLM)
// ve aux servisler kaldi.
const TRACKED_PROVIDERS = ["deepseek", "openai", "dataforseo"] as const;

async function fetchSpof(): Promise<DashboardSpofCard[]> {
  // /api/api-keys returns one provider's list at a time; we ask for all
  // three in parallel and only keep providers whose SPOF level is
  // warning/critical (ok ones don't deserve dashboard real estate).
  const responses = await Promise.allSettled(
    TRACKED_PROVIDERS.map((p) =>
      apiClient
        .get<PoolStatsResponse>("/api/api-keys", { params: { provider: p } })
        .then((r) => r.data),
    ),
  );

  const cards: DashboardSpofCard[] = [];
  responses.forEach((r, idx) => {
    if (r.status !== "fulfilled") return;
    const spof = r.value?.stats?.spof;
    if (!spof) return;
    if (spof.level === "ok") return;
    const provider = TRACKED_PROVIDERS[idx];
    if (!provider) return;
    cards.push({
      provider,
      level: spof.level,
      active_keys: spof.active_keys,
      min_recommended: spof.min_recommended,
      message: spof.message,
    });
  });
  // Critical first, then warning — operator should see the worst row at top.
  cards.sort((a, b) => (a.level === "critical" ? -1 : 1));
  return cards;
}

export const dashboardApi = {
  fetchAll: async (): Promise<DashboardCardSet> => {
    const [users, revenue, usage, health, spof] = await Promise.all([
      apiClient.get("/api/admin/analytics/users", { params: { days: 30 } }).then((r) => r.data),
      apiClient.get("/api/admin/analytics/revenue").then((r) => r.data),
      apiClient.get("/api/admin/analytics/usage", { params: { days: 30 } }).then((r) => r.data),
      apiClient.get("/api/admin/health/detailed").then((r) => r.data),
      fetchSpof().catch(() => [] as DashboardSpofCard[]),
    ]);
    return { users, revenue, usage, health, spof };
  },
};
